export const colors = {
  primary: {
    50: '#F2F1FF',
    100: '#E4E2FF',
    200: '#C9C5FF',
    300: '#ADA8FF',
    400: '#928BFF',
    500: '#766EFF',
    600: '#6129FF', // Main brand color
    700: '#4E21CC',
    800: '#3B1999',
    900: '#281166',
  },
  gray: {
    50: '#FCFCFD',
    100: '#F9FAFB',
    200: '#F3F4F6',
    300: '#E5E7EB',
    400: '#D1D5DB',
    500: '#9CA3AF',
    600: '#6B7280',
    700: '#4B5563',
    800: '#1F2937',
    900: '#111827',
  },
  success: {
    50: '#ECFDF5',
    500: '#10B981',
    600: '#059669',
  },
  warning: {
    50: '#FFFBEB',
    500: '#F59E0B',
    600: '#D97706',
  },
  error: {
    50: '#FEF2F2',
    500: '#EF4444',
    600: '#DC2626',
  },
  background: {
    light: 'linear-gradient(180deg, #ffffff 0%, rgb(252, 252, 253) 100%)',
    dark: '#050505',
  },
};

export const typography = {
  fontFamily: {
    sans: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      'Oxygen',
      'Ubuntu',
      'Cantarell',
      '"Open Sans"',
      '"Helvetica Neue"',
      'sans-serif',
    ],
  },
  fontWeight: {
    light: '300',
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800',
  },
  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
    '6xl': '3.75rem',
  },
  lineHeight: {
    none: '1',
    tight: '1.25',
    snug: '1.375',
    normal: '1.5',
    relaxed: '1.625',
    loose: '2',
  },
};

export const spacing = {
  px: '1px',
  0: '0',
  0.5: '0.125rem',
  1: '0.25rem',
  1.5: '0.375rem',
  2: '0.5rem',
  2.5: '0.625rem',
  3: '0.75rem',
  3.5: '0.875rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  7: '1.75rem',
  8: '2rem',
  9: '2.25rem',
  10: '2.5rem',
  12: '3rem',
  14: '3.5rem',
  16: '4rem',
  20: '5rem',
  24: '6rem',
  28: '7rem',
  32: '8rem',
  36: '9rem',
  40: '10rem',
  44: '11rem',
  48: '12rem',
  52: '13rem',
  56: '14rem',
  60: '15rem',
  64: '16rem',
  72: '18rem',
  80: '20rem',
  96: '24rem',
};

export const buttonVariants = {
  solid: {
    base: 'inline-flex items-center justify-center rounded-lg font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
    variants: {
      primary: 'bg-primary-600 text-white hover:bg-primary-700 focus-visible:ring-primary-500',
      secondary: 'bg-gray-800 text-white hover:bg-gray-900 focus-visible:ring-gray-500',
      success: 'bg-success-600 text-white hover:bg-success-700 focus-visible:ring-success-500',
      error: 'bg-error-600 text-white hover:bg-error-700 focus-visible:ring-error-500',
    },
  },
  outline: {
    base: 'inline-flex items-center justify-center rounded-lg border-8 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
    variants: {
      primary: 'border-primary-600 text-primary-600 hover:bg-primary-50 focus-visible:ring-primary-500',
      secondary: 'border-gray-800 text-gray-800 hover:bg-gray-50 focus-visible:ring-gray-500',
      success: 'border-success-600 text-success-600 hover:bg-success-50 focus-visible:ring-success-500',
      error: 'border-error-600 text-error-600 hover:bg-error-50 focus-visible:ring-error-500',
    },
  },
  ghost: {
    base: 'inline-flex items-center justify-center rounded-lg font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
    variants: {
      primary: 'text-primary-600 hover:bg-primary-50 focus-visible:ring-primary-500',
      secondary: 'text-gray-800 hover:bg-gray-50 focus-visible:ring-gray-500',
      success: 'text-success-600 hover:bg-success-50 focus-visible:ring-success-500',
      error: 'text-error-600 hover:bg-error-50 focus-visible:ring-error-500',
    },
  },
  link: {
    base: 'inline-flex items-center justify-center font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
    variants: {
      primary: 'text-primary-600 hover:text-primary-700 underline-offset-4 hover:underline',
      secondary: 'text-gray-800 hover:text-gray-900 underline-offset-4 hover:underline',
      success: 'text-success-600 hover:text-success-700 underline-offset-4 hover:underline',
      error: 'text-error-600 hover:text-error-700 underline-offset-4 hover:underline',
    },
  },
};

export const theme = {
  colors: colors,
  typography: typography,
  spacing: spacing,
  buttonVariants: buttonVariants,
  breakpoints: {
    xs: '320px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    DEFAULT: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    '2xl': '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  },
  radii: {
    none: '0',
    sm: '0.125rem',
    DEFAULT: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
    '3xl': '1.5rem',
    full: '9999px',
  },
};

export type Theme = typeof theme; 